import { z } from 'zod'

import { SocketBulkActionsTypes } from 'apps/chat/constants/LiveChatInterfaces'
import { UserRoles } from 'common/userRoles'
import { WebSocketEvents } from 'constants/WebSockets'
import {
  chatThreadUserSchema,
  liveChatNoteSchema,
  threadSchema,
} from 'shared/api/requests/chat/threads/schemas'
import { SocketEventBaseSchema } from 'utils/services/notificationsService/constants'

import { ChatMessageType } from '../types/chatMessageTypeList'

const liveChatThreadSchema = threadSchema.extend({
  notes: z.array(liveChatNoteSchema).optional(),
  score_rating: z.number().optional(),
})

const createLcSentSchema = <M extends z.ZodObject<TSFixMe>, T extends z.ZodType>(
  modelSchema: M,
  type: T,
) =>
  z.object({
    message_id: z.string(),
    page_id: z.string(),
    type: type,
    user_id: z.string(),
    sort_id: z.number(),
    timestamp: z.number(),
    model: modelSchema,
  })

const assignEventSchema = createLcSentSchema(
  z.object({
    from_user_id: z.null(),
    from_user_name: z.null(),
    to_user_id: z.number(),
    to_user_name: z.string(),
  }),
  z.literal(ChatMessageType.ASSIGN),
)

const reassignEventSchema = createLcSentSchema(
  z.object({
    from_user_id: z.null(),
    from_user_name: z.null(),
    to_user_id: z.number(),
    to_user_name: z.string(),
  }),
  z.literal(ChatMessageType.REASSIGN),
)

const userThreadNewEventSchema = createLcSentSchema(
  z.object({
    before: z.string(),
  }),
  z.literal(ChatMessageType.USER_THREAD_NEW),
)

const threadUserReadData = {
  event: z.literal(WebSocketEvents.THREAD_USER_READ),
  id: z.string(),
  account_id: z.number(),
  ts_last_read: z.number().nullable(),
  ts_ig_last_read: z.number().nullable(),
  ts_tt_last_read: z.number().nullable(),
}

export const ThreadsSocketSchema = {
  threadUpdated: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_UPDATED),
    model: liveChatThreadSchema,
  }),
  threadRead: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_READ),
    model: liveChatThreadSchema,
  }),
  subscriberUpdated: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.SUBSCRIBER_UPDATED),
    model: chatThreadUserSchema,
  }),
  threadMessaged: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_MESSAGED),
    model: liveChatThreadSchema,
  }),
  threadClosed: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_CLOSED),
    model: z.array(z.number()),
  }),
  threadOpened: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_OPENED),
    model: z.array(z.number()),
  }),
  threadUnassigned: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_UNASSIGNED),
    model: z.array(z.number()),
  }),
  threadAssigned: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_ASSIGNED),
    model: z.array(z.number()),
  }),
  threadUserRead: SocketEventBaseSchema.extend(threadUserReadData),
  threadsFilterChanged: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.LC_THREADS_FILTER_CHANGED),
    model: z.object({
      value: z.boolean(),
    }),
  }),
  accountUserRoleUpdate: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.ACCOUNT_USER_ROLE_UPDATE),
    user_id: z.number().nullable(),
    new_role: z.enum(UserRoles),
  }),
  lcSent: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.LC_SENT),
    model: z
      .discriminatedUnion('type', [
        assignEventSchema,
        reassignEventSchema,
        userThreadNewEventSchema,
      ])
      .or(liveChatNoteSchema)
      .or(z.any()),
  }),
  threadBulkActionComplete: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_BULK_ACTION_COMPLETE),
    user_id: z.number().nullish(),
    action_type: z.enum(SocketBulkActionsTypes).optional(),
  }),
  // Not reproduced
  threadControlErrorReset: SocketEventBaseSchema.extend({
    event: z.literal(WebSocketEvents.THREAD_CONTROL_ERROR_RESET),
    model: z.object({
      subscriber_id: z.number().nullable(),
    }),
  }),
}

const threadUserReadSchema = z.object(threadUserReadData)

export type ThreadsSocketData = z.infer<typeof liveChatThreadSchema>
export type AssignEvent = z.infer<typeof assignEventSchema>
export type UserThreadNewEvent = z.infer<typeof userThreadNewEventSchema>
export type LiveChatNote = z.infer<typeof liveChatNoteSchema>
export type LCSentSocketModel = z.infer<typeof ThreadsSocketSchema.lcSent>['model']
export type ThreadUserReadSocketData = z.infer<typeof threadUserReadSchema>
